import { isUselessUnityRef } from "./unityEvents";

export const COMING_SOON_TAG = "Putting the finishing touches on this one. Available soon.";

export const ROOM_HOTSPOT_POPUPS = {
  desk: {
    title: "Desk: Your Active Work Space",
    body:
      "This is where the work in motion lives, the things you're actively doing today. Drafts you're shaping, tasks you're moving forward, steps you're breaking down. Everything stays exactly where you left it, so when you return to the room, you can pick up right where you stopped.",
    showTag: false,
    opensPanelOnClose: true,
    firstTimeOnly: true,
  },
  whiteboard: {
    title: "Whiteboard: Where ideas take shape",
    body:
      "This is your mental sandbox, sketch, brainstorm, map steps, or upload anything that helps you think: photos, notes, videos, scribbles. Give your thoughts room to breathe here. Work things out with full context before deciding where they belong. Add something to your project, store it on the bookshelf, or leave it here until you're ready.",
    showTag: false,
    opensPanelOnClose: true,
    firstTimeOnly: true,
  },
  bookshelf: {
    title: "Bookshelf: Your Project's Memory",
    body:
      "Your essential artifacts live here, versions, drafts, notes, and the references that shape this room's work. Everything stays organized and easy to find. If you need something, simply ask your AI coach. It will highlight the relevant items so you can click and view them instantly. Your master document lives here too, the most current version of your work, ready for deep focus whenever you choose.",
    showTag: false,
    opensPanelOnClose: true,
    firstTimeOnly: true,
  },
  couch: {
    title: "Couch: Rest, reflect, or talk it through",
    body:
      "Sometimes momentum comes from pausing. Sit here to let ideas settle, zoom out, or talk things through with your coach. Use this space however you need: a quiet moment, a guided reflection, a mental reset, or even a few minutes of your favorite show to clear your head. Nothing moves without you. When you come back, everything in the room is exactly where you left it.",
    showTag: true,
  },
  painting: {
    title: "Painting: Personalize your space",
    body:
      "You're in control of everything you see and feel here, the art, the colors, the music, even the sky outside. Design the room to match your mindset, your workflow, your season of life. It can be calm, bright, creative, minimal, or something totally unique to you. Your space should work the way you do.",
    showTag: true,
  },
  calendar: {
    title: "Calendar and Bulletin Board",
    body:
      "Your day and your to-dos in one place. See what's scheduled for this project, or open your full calendar when you want the whole picture. Your bulletin board sits alongside it, holding the running list of what needs doing. Ask Lumen to add a to-do or an event and it shows up here. Connect the tools you already use and everything lives in one view.",
    showTag: false,
    opensPanelOnClose: true,
    firstTimeOnly: true,
  },
  bulletin_board: {
    title: "Bulletin Board: Your To-Do List",
    body:
      "Action items and to-dos pinned where you can see them. Ask Lumen to add steps from chat and they'll land here automatically.",
    showTag: false,
    opensPanelOnClose: true,
    firstTimeOnly: true,
  },
};

export const HAVEN_SPACE_POPUPS = {
  lighthouse: {
    title: "The Lighthouse",
    body:
      "Mission control for your whole world. It's where Lumen lives and where you get your bearings. When you don't know where to go or what to do next, you come here.",
    showTag: true,
  },
  observatory: {
    title: "The Observatory",
    body:
      "Where you can see yourself clearly. A calm view of how your work is going, reflected back without judgment, so you understand your own patterns.",
    showTag: true,
  },
  cafe: {
    title: "The Cafe",
    body:
      "The only place you're not alone. Connect with other people or with Lumen and body double, work side by side, get the quiet accountability of someone else in the room.",
    showTag: true,
  },
  grove: {
    title: "The Grove",
    body:
      "Your nervous system reset. Torii gate, cherry blossoms, a zen garden you can sink into. When your mind is racing, this is where you come to slow down and breathe.",
    showTag: true,
  },
  rec_room: {
    title: "The Rec Room",
    body:
      "Pure low-pressure fun. Billiards, arcade, a rooftop lounge, a private cinema. No tasks, no goals, just permission to decompress.",
    showTag: true,
  },
  workshop: {
    title: "The Workshop",
    body:
      "Where you create and customize rooms, buildings, and assets, and eventually exchange them with other players. The builder's space for shaping your own world.",
    showTag: true,
  },
  library: {
    title: "The Library",
    body:
      "Courses and resources, all in one place. Where you go to learn, look something up, or find the material you need.",
    showTag: true,
  },
  lab: {
    title: "The Lab",
    body:
      "Where you learn by doing instead of just reading about it. A hands-on space to practice, try things out, and build real skills with Lumen alongside you. Less theory, more doing.",
    showTag: true,
  },
  gray_havens: {
    title: "The Archives",
    body:
      "Where your finished and paused work is kept. Reached through the portal at the edge of Haven. Nothing is deleted here, it's set aside and honored, ready whenever you want to return to it.",
    showTag: true,
  },
};

/** Canonical building names for seeding world_json (Unity + popups). */
export const HAVEN_SPACE_BUILDING_NAMES = {
  lighthouse: "The Lighthouse",
  observatory: "The Observatory",
  cafe: "The Cafe",
  grove: "The Grove",
  rec_room: "The Rec Room",
  workshop: "The Workshop",
  library: "The Library",
  lab: "The Lab",
  gray_havens: "Gray Havens",
};

export function getHotspotPopupContent(id) {
  return ROOM_HOTSPOT_POPUPS[id] || HAVEN_SPACE_POPUPS[id] || null;
}

export function isHavenSpacePopup(id) {
  return Boolean(HAVEN_SPACE_POPUPS[id]);
}

function havenSlug(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/^the\s+/, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

/** True when island is Haven (personal island with space buildings). */
export function isHavenIsland(island) {
  if (!island) return false;
  const name = String(island.name || "").toLowerCase();
  const id = String(island.id || "").toLowerCase();
  return name.includes("haven") || id.includes("haven");
}

/**
 * Map a Haven-island building → HAVEN_SPACE_POPUPS id.
 * Skips the main "Haven" work building (Lounge etc.) — interior uses room hotspots.
 */
export function havenPopupIdFromBuilding(building) {
  if (!building) return null;
  if (havenSlug(building.name) === "haven") return null;

  const idSlug = havenSlug(building.id);
  const nameSlug = havenSlug(building.name);

  if (HAVEN_SPACE_POPUPS[idSlug]) return idSlug;
  if (HAVEN_SPACE_POPUPS[nameSlug]) return nameSlug;

  for (const key of Object.keys(HAVEN_SPACE_POPUPS)) {
    if (idSlug.includes(key) || nameSlug.includes(key)) return key;
    const spaced = key.replace(/_/g, " ");
    if (String(building.name || "").toLowerCase().includes(spaced)) return key;
  }
  return null;
}

/** Unity scene / surfaceID strings → canonical HAVEN_SPACE_POPUPS keys. */
const HAVEN_POPUP_ALIASES = {
  zen_garden: "grove",
  zen_gardens: "grove",
  the_grove: "grove",
  grove: "grove",
  archives: "gray_havens",
  the_archives: "gray_havens",
  archive: "gray_havens",
  gray_havens: "gray_havens",
  gray_haven: "gray_havens",
  grayhavens: "gray_havens",
  arc:"gray_havens"
};

function resolveHavenPopupAlias(ref) {
  if (isUselessUnityRef(ref)) return null;
  const slug = havenSlug(ref);
  if (HAVEN_POPUP_ALIASES[slug]) return HAVEN_POPUP_ALIASES[slug];
  if (HAVEN_SPACE_POPUPS[slug]) return slug;

  for (const [alias, popupId] of Object.entries(HAVEN_POPUP_ALIASES)) {
    if (slug.includes(alias) || alias.includes(slug)) return popupId;
  }
  for (const key of Object.keys(HAVEN_SPACE_POPUPS)) {
    if (slug.includes(key) || key.includes(slug)) return key;
  }
  return null;
}

/** Match a raw Unity ref / building id string to a Haven popup id. */
export function havenPopupIdFromRef(ref) {
  if (isUselessUnityRef(ref)) return null;
  const raw = String(ref).trim();
  if (isHavenSpacePopup(raw)) return raw;
  return resolveHavenPopupAlias(raw);
}

function collectHavenCandidates(value, out, depth = 0) {
  if (depth > 5 || value == null) return;
  if (typeof value === "string") {
    if (!isUselessUnityRef(value)) out.add(value.trim());
    return;
  }
  if (typeof value !== "object") return;

  for (const key of [
    "name",
    "buildingName",
    "buildingID",
    "buildingId",
    "hotspotID",
    "hotspotId",
    "surfaceID",
    "surfaceId",
    "surface",
    "id",
    "label",
    "title",
    "gameObjectName",
    "objectName",
    "targetName",
    "hitName",
  ]) {
    if (!isUselessUnityRef(value[key])) out.add(String(value[key]).trim());
  }
  for (const entry of Object.values(value)) {
    if (typeof entry === "string") {
      if (!isUselessUnityRef(entry)) out.add(entry.trim());
    } else if (entry && typeof entry === "object") {
      collectHavenCandidates(entry, out, depth + 1);
    }
  }
}

/** Match Haven popup id from any string field in a Unity payload. */
export function resolveHavenPopupFromUnityPayload(hotspotId, payload, findBuildingRecord) {
  const candidates = new Set();
  if (!isUselessUnityRef(hotspotId)) candidates.add(String(hotspotId).trim());
  collectHavenCandidates(payload, candidates);

  for (const candidate of candidates) {
    const fromRef = havenPopupIdFromRef(candidate);
    if (fromRef) return fromRef;

    if (findBuildingRecord) {
      const fromBuilding = havenPopupIdFromBuilding(findBuildingRecord(candidate)?.building);
      if (fromBuilding) return fromBuilding;
    }

    const lower = candidate.toLowerCase();
    const fromAlias = resolveHavenPopupAlias(candidate);
    if (fromAlias) return fromAlias;

    for (const key of Object.keys(HAVEN_SPACE_POPUPS)) {
      const spaced = key.replace(/_/g, " ");
      if (lower === key || lower.includes(spaced) || lower.includes(key.replace(/_/g, ""))) {
        return key;
      }
    }
  }

  return null;
}

/** Map Unity HotspotClicked / building id to a Haven space popup id, if any. */
export function havenPopupIdFromUnityRef(hotspotRef, findBuildingRecord) {
  const fromRef = havenPopupIdFromRef(hotspotRef);
  if (fromRef) return fromRef;

  const record = findBuildingRecord?.(hotspotRef);
  if (record) return havenPopupIdFromBuilding(record.building);
  return null;
}

/** Map Unity / JSON surface strings → React hotspot popup id. */
const SURFACE_POPUP_ALIASES = {
  desk: "desk",
  table: "desk",
  bookcase: "bookshelf",
  bookshelf: "bookshelf",
  whiteboard: "whiteboard",
  couch: "couch",
  painting: "painting",
  paint: "painting",
  calendar: "calendar",
  bulletin: "calendar",
  bulletin_board: "calendar",
}

export function hotspotIdFromSurface(surface) {
  const raw = String(surface || "").toLowerCase().trim();
  if (!raw) return null;
  if (SURFACE_POPUP_ALIASES[raw]) return SURFACE_POPUP_ALIASES[raw];
  if (ROOM_HOTSPOT_POPUPS[raw]) return raw;
  for (const [key, id] of Object.entries(SURFACE_POPUP_ALIASES)) {
    if (raw.includes(key)) return id;
  }
  return null;
}

/** Resolve Unity HotspotClicked ref to a popup id. */
export function hotspotIdFromUnityRef(hotspotRef, findSurfaceItemById) {
  const item = findSurfaceItemById?.(hotspotRef);
  if (item?.surface) return hotspotIdFromSurface(item.surface);

  if (isUselessUnityRef(hotspotRef)) return null;
  const ref = String(hotspotRef).trim();

  const colonIdx = ref.indexOf(":");
  if (colonIdx > 0) {
    const fromColon = hotspotIdFromSurface(ref.slice(colonIdx + 1));
    if (fromColon) return fromColon;
  }

  return hotspotIdFromSurface(ref) || (getHotspotPopupContent(ref) ? ref : null);
}
